import React, { useCallback, useEffect, useState } from 'react';

import { apiGet } from '../Api';
import './PublicDocument.css';
import PublicShell from './PublicShell';

function normalizeEntry(entry) {
    return {
        id: entry.id ?? entry.Id ?? `${entry.section ?? entry.Section}-${entry.entryOrder ?? entry.EntryOrder}`,
        section: String(entry.section ?? entry.Section ?? 'Updates'),
        type: String(entry.type ?? entry.Type ?? 'Changed'),
        text: String(entry.text ?? entry.Text ?? ''),
    };
}

export function groupReleaseNotes(entries) {
    const sections = [];
    const byName = new Map();

    entries.map(normalizeEntry).forEach((entry) => {
        if (!byName.has(entry.section)) {
            const section = { name: entry.section, entries: [] };
            byName.set(entry.section, section);
            sections.push(section);
        }
        byName.get(entry.section).entries.push(entry);
    });

    return sections;
}

function entryText(entry) {
    const prefix = `${entry.type} `;
    return entry.text.startsWith(prefix) ? entry.text.slice(prefix.length) : entry.text;
}

export default function ReleaseNotes() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiGet('/changelog', false);
            const result = await response.json();
            setEntries(Array.isArray(result) ? result : []);
        } catch {
            setEntries([]);
            setError('Release notes could not be loaded. Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const sections = groupReleaseNotes(entries);

    return (
        <PublicShell document>
            <article className="public-document release-notes" aria-labelledby="release-notes-title">
                <header className="public-document__header">
                    <p className="public-document__eyebrow">Field updates</p>
                    <h1 id="release-notes-title">Release notes</h1>
                    <p className="public-document__intro">
                        New equipment, repaired systems, and the occasional lesson learned the hard way.
                    </p>
                </header>

                {loading && (
                    <section className="public-document__state" aria-live="polite" aria-busy="true">
                        <h2>Loading dispatches</h2>
                        <p>Checking the latest field reports…</p>
                    </section>
                )}

                {!loading && error && (
                    <section className="public-document__state" role="alert">
                        <h2>Signal lost</h2>
                        <p>{error}</p>
                        <button className="public-document__button" type="button" onClick={load}>
                            Try again
                        </button>
                    </section>
                )}

                {!loading && !error && sections.length === 0 && (
                    <section className="public-document__state">
                        <h2>No public dispatches yet</h2>
                        <p>There are no published release notes right now. Check back after the next expedition.</p>
                    </section>
                )}

                {!loading && !error && sections.map((section) => (
                    <section className="public-document__section" key={section.name}>
                        <h2>{section.name}</h2>
                        <ul className="release-notes__list">
                            {section.entries.map((entry) => (
                                <li className="release-notes__entry" key={entry.id}>
                                    <span
                                        className="release-notes__type"
                                        data-type={entry.type.toLowerCase()}
                                    >
                                        {entry.type}
                                    </span>
                                    <span>{entryText(entry)}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </article>
        </PublicShell>
    );
}
